import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { endWebRtcSession, getSocket, joinRoom, leaveRoom, signalPeer } from '../services/socket';
import { createPhonePairingUrl } from '../utils/publicOrigin';
import { publicEnv } from '../config/env';
import { getIceServers } from '../services/webrtcConfig';
import { releaseMediaSession } from '../utils/mediaSession';

export type PhoneConnectionStatus = 'waiting' | 'connecting' | 'connected' | 'reconnecting' | 'disconnected' | 'failed' | 'closed' | 'ended';

interface PhonePairingValue {
  roomId: string;
  pairingUrl: string;
  remoteStream?: MediaStream;
  status: PhoneConnectionStatus;
  cameraSwitching?: boolean;
  reconnect: () => void;
  disconnect: () => void;
  endSession: () => void;
}

const PhonePairingContext = createContext<PhonePairingValue | undefined>(undefined);
const MAX_RECONNECT_ATTEMPTS = 4;

function getRoomId() {
  const stored = sessionStorage.getItem('examPhoneRoomId');
  if (stored) return stored;
  const created = `exam-${crypto.randomUUID()}`;
  sessionStorage.setItem('examPhoneRoomId', created);
  return created;
}

export function PhonePairingProvider({ children }: { children: ReactNode }) {
  const [roomId] = useState(getRoomId);
  const [remoteStream, setRemoteStream] = useState<MediaStream>();
  const [status, setStatus] = useState<PhoneConnectionStatus>('waiting');
  const [cameraSwitching, setCameraSwitching] = useState(false);
  const [iceServers, setIceServers] = useState<RTCIceServer[]>([{ urls: 'stun:stun.l.google.com:19302' }]);
  const peerRef = useRef<RTCPeerConnection>();
  const retryRef = useRef(0);
  const retryTimerRef = useRef<number>();
  const intentionallyClosedRef = useRef(false);
  const endedRef = useRef(false);
  const remoteStreamRef = useRef<MediaStream>();
  const switchTimerRef = useRef<number>();

  const pairingUrl = useMemo(
    () => createPhonePairingUrl(roomId, publicEnv.publicAppUrl, window.location.origin),
    [roomId]
  );

  const shutdown = useCallback((ended: boolean) => {
    if (ended && endedRef.current) return;
    intentionallyClosedRef.current = true;
    endedRef.current = ended;
    releaseMediaSession({
      stream: remoteStreamRef.current,
      peer: peerRef.current,
      retryTimer: retryTimerRef.current
    });
    retryTimerRef.current = undefined;
    if (switchTimerRef.current) window.clearTimeout(switchTimerRef.current);
    switchTimerRef.current = undefined;
    setCameraSwitching(false);
    peerRef.current = undefined;
    remoteStreamRef.current = undefined;
    leaveRoom(roomId);
    setRemoteStream(undefined);
    setStatus(ended ? 'ended' : 'closed');
  }, [roomId]);

  const endSession = useCallback(() => {
    if (endedRef.current) return;
    endWebRtcSession(roomId);
    shutdown(true);
  }, [roomId, shutdown]);

  const createPeer = useCallback(() => {
    if (typeof RTCPeerConnection === 'undefined' || intentionallyClosedRef.current) {
      setStatus('failed');
      return undefined;
    }
    peerRef.current?.close();
    const peer = new RTCPeerConnection({ iceServers });
    peerRef.current = peer;
    peer.ontrack = (event) => {
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      remoteStreamRef.current = stream;
      setRemoteStream(stream);
      retryRef.current = 0;
      setStatus('connected');
    };
    peer.onicecandidate = (event) => {
      if (event.candidate) signalPeer(roomId, { candidate: event.candidate });
    };
    peer.onconnectionstatechange = () => {
      const state = peer.connectionState;
      if (state === 'connected') setStatus('connected');
      else if (state === 'connecting' || state === 'new') setStatus('connecting');
      else if (state === 'disconnected' || state === 'failed') {
        setStatus(retryRef.current < MAX_RECONNECT_ATTEMPTS ? 'reconnecting' : 'disconnected');
        if (retryRef.current < MAX_RECONNECT_ATTEMPTS && !retryTimerRef.current) {
          const delay = Math.min(8000, 1000 * 2 ** retryRef.current++);
          retryTimerRef.current = window.setTimeout(() => {
            retryTimerRef.current = undefined;
            createPeer();
            joinRoom(roomId);
          }, delay);
        }
      } else if (state === 'closed' && intentionallyClosedRef.current) setStatus('closed');
    };
    return peer;
  }, [iceServers, roomId]);

  useEffect(() => { void getIceServers().then(setIceServers); }, []);

  useEffect(() => {
    const socket = getSocket();
    const peer = createPeer();
    joinRoom(roomId);
    const handleConnect = () => {
      if (intentionallyClosedRef.current) return;
      joinRoom(roomId);
      if (status === 'disconnected') setStatus('reconnecting');
    };
    const handleSignal = async ({ payload }: { payload: RTCSessionDescriptionInit & { candidate?: RTCIceCandidateInit; control?: 'camera-switching' | 'camera-switched' } }) => {
      if (payload.control) {
        setCameraSwitching(true);
        if (switchTimerRef.current) window.clearTimeout(switchTimerRef.current);
        switchTimerRef.current = window.setTimeout(() => setCameraSwitching(false), payload.control === 'camera-switched' ? 6000 : 12_000);
        return;
      }
      const activePeer = peerRef.current ?? createPeer();
      if (!activePeer) return;
      try {
        setStatus('connecting');
        if (payload.candidate) await activePeer.addIceCandidate(payload.candidate);
        else if (payload.type === 'offer') {
          await activePeer.setRemoteDescription(payload);
          const answer = await activePeer.createAnswer();
          await activePeer.setLocalDescription(answer);
          signalPeer(roomId, answer);
        }
      } catch {
        setStatus('failed');
      }
    };
    const handleSessionEnded = () => shutdown(true);
    socket.on('connect', handleConnect);
    socket.on('webrtc:signal', handleSignal);
    socket.on('webrtc:session-ended', handleSessionEnded);
    return () => {
      socket.off('connect', handleConnect);
      socket.off('webrtc:signal', handleSignal);
      socket.off('webrtc:session-ended', handleSessionEnded);
      releaseMediaSession({ peer, retryTimer: retryTimerRef.current });
      if (switchTimerRef.current) window.clearTimeout(switchTimerRef.current);
    };
    // Provider is intentionally mounted for the full application session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createPeer, roomId, shutdown]);

  useEffect(() => () => {
    if (remoteStreamRef.current && !endedRef.current) endWebRtcSession(roomId);
    releaseMediaSession({ stream: remoteStreamRef.current, peer: peerRef.current, retryTimer: retryTimerRef.current });
    leaveRoom(roomId);
  }, [roomId]);

  useEffect(() => {
    const handlePageExit = () => {
      if (!intentionallyClosedRef.current && remoteStreamRef.current) endWebRtcSession(roomId);
      shutdown(true);
    };
    window.addEventListener('pagehide', handlePageExit);
    window.addEventListener('beforeunload', handlePageExit);
    return () => {
      window.removeEventListener('pagehide', handlePageExit);
      window.removeEventListener('beforeunload', handlePageExit);
    };
  }, [roomId, shutdown]);

  const reconnect = useCallback(() => {
    intentionallyClosedRef.current = false;
    endedRef.current = false;
    retryRef.current = 0;
    setStatus('reconnecting');
    createPeer();
    joinRoom(roomId);
  }, [createPeer, roomId]);

  const disconnect = useCallback(() => {
    shutdown(false);
  }, [shutdown]);

  const value = useMemo(() => ({ roomId, pairingUrl, remoteStream, status, cameraSwitching, reconnect, disconnect, endSession }), [cameraSwitching, disconnect, endSession, pairingUrl, reconnect, remoteStream, roomId, status]);
  return <PhonePairingContext.Provider value={value}>{children}</PhonePairingContext.Provider>;
}

export function usePhonePairing() {
  const value = useContext(PhonePairingContext);
  if (!value) throw new Error('usePhonePairing must be used inside PhonePairingProvider');
  return value;
}
